import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import {
  TrendingUp,
  Wallet,
  FileWarning,
  Users,
  PiggyBank,
  Building2,
  HandCoins,
  ArrowRight,
} from "lucide-react";
import api from "@/lib/api";
import { formatRupiah, formatDateShort } from "@/lib/format";
import PageHeader from "@/components/PageHeader";
import StatCard from "@/components/StatCard";
import StatusBadge from "@/components/StatusBadge";
import { CardSkeleton } from "@/components/Skeletons";
import { Skeleton } from "@/components/ui/skeleton";

const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-lg border border-border bg-card p-3 text-xs shadow-md">
      <p className="mb-1 font-semibold">{label}</p>
      {payload.map((p) => (
        <p key={p.dataKey} style={{ color: p.color }}>
          {p.name}: {formatRupiah(p.value)}
        </p>
      ))}
    </div>
  );
};

const Dashboard = () => {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/dashboard/stats").then((r) => setData(r.data)).catch(() => {});
  }, []);

  if (!data) {
    return (
      <div className="space-y-6">
        <PageHeader title="Dashboard" subtitle="Ringkasan keuangan usaha Anda" />
        <CardSkeleton count={4} />
        <div className="grid gap-6 lg:grid-cols-3">
          <Skeleton className="h-80 lg:col-span-2" />
          <Skeleton className="h-80" />
        </div>
      </div>
    );
  }

  const stats = data.invoice_stats || {};

  return (
    <div className="space-y-6">
      <PageHeader title="Dashboard" subtitle="Ringkasan keuangan usaha Anda" />

      <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
        <StatCard testId="stat-sales-month" title="Penjualan Bulan Ini" value={formatRupiah(data.sales_month)} icon={TrendingUp} accent="primary" hint={`Hari ini: ${formatRupiah(data.sales_today)}`} />
        <StatCard testId="stat-unpaid" title="Invoice Belum Bayar" value={formatRupiah(data.unpaid_total)} icon={FileWarning} accent="warning" hint={`${data.unpaid_count} invoice`} />
        <StatCard testId="stat-expense" title="Pengeluaran Bulan Ini" value={formatRupiah(data.expense_month)} icon={Wallet} accent="danger" />
        <StatCard testId="stat-profit" title="Laba Bulan Ini" value={formatRupiah(data.profit_month)} icon={PiggyBank} accent="success" />
      </div>

      <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
        <StatCard testId="stat-income" title="Pemasukan Lain Bulan Ini" value={formatRupiah(data.income_month)} icon={HandCoins} accent="accent" hint="Di luar invoice" />
        <StatCard testId="stat-customers" title="Total Customer" value={data.customer_count} icon={Users} accent="accent" />
        <StatCard testId="stat-assets" title="Total Asset Usaha" value={formatRupiah(data.asset_total)} icon={Building2} accent="primary" />
        <StatCard testId="stat-invoice-paid" title="Invoice Lunas" value={stats.paid || 0} icon={TrendingUp} accent="success" hint={`${stats.pending || 0} pending · ${stats.draft || 0} draft`} />
      </div>

      {/* Charts row */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="rounded-xl border border-border bg-card p-6 shadow-[0_2px_10px_rgba(0,0,0,0.03)] lg:col-span-2">
          <h3 className="font-heading text-lg font-semibold">Penjualan vs Pengeluaran</h3>
          <p className="mb-4 text-sm text-muted-foreground">6 bulan terakhir</p>
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={data.chart}>
              <defs>
                <linearGradient id="cSales" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="hsl(var(--chart-1))" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="hsl(var(--chart-1))" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="cExp" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="hsl(var(--chart-5))" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="hsl(var(--chart-5))" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
              <XAxis dataKey="month" stroke="hsl(var(--muted-foreground))" fontSize={12} tickLine={false} axisLine={false} />
              <YAxis stroke="hsl(var(--muted-foreground))" fontSize={11} tickLine={false} axisLine={false} tickFormatter={(v) => (v >= 1000000 ? `${v / 1000000}jt` : v >= 1000 ? `${v / 1000}rb` : v)} width={48} />
              <Tooltip content={<ChartTooltip />} />
              <Area type="monotone" dataKey="penjualan" name="Penjualan" stroke="hsl(var(--chart-1))" strokeWidth={2.5} fill="url(#cSales)" />
              <Area type="monotone" dataKey="pengeluaran" name="Pengeluaran" stroke="hsl(var(--chart-5))" strokeWidth={2.5} fill="url(#cExp)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        <div className="rounded-xl border border-border bg-card p-6 shadow-[0_2px_10px_rgba(0,0,0,0.03)]">
          <h3 className="font-heading text-lg font-semibold">Statistik Pengeluaran</h3>
          <p className="mb-4 text-sm text-muted-foreground">Per bulan</p>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={data.chart}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
              <XAxis dataKey="month" stroke="hsl(var(--muted-foreground))" fontSize={12} tickLine={false} axisLine={false} />
              <YAxis hide />
              <Tooltip content={<ChartTooltip />} cursor={{ fill: "hsl(var(--secondary))" }} />
              <Bar dataKey="pengeluaran" name="Pengeluaran" fill="hsl(var(--chart-2))" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Recent lists */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div className="rounded-xl border border-border bg-card p-6 shadow-[0_2px_10px_rgba(0,0,0,0.03)]">
          <div className="mb-4 flex items-center justify-between">
            <h3 className="font-heading text-lg font-semibold">Transaksi Terbaru</h3>
            <Link to="/invoices" className="flex items-center gap-1 text-sm font-medium text-primary hover:underline">
              Lihat semua <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </div>
          {data.recent_invoices.length === 0 ? (
            <p className="py-8 text-center text-sm text-muted-foreground">Belum ada transaksi</p>
          ) : (
            <div className="space-y-1">
              {data.recent_invoices.map((inv) => (
                <Link
                  key={inv.id}
                  to={`/invoices/${inv.id}`}
                  className="flex items-center justify-between rounded-lg px-3 py-2.5 transition-colors hover:bg-secondary"
                >
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium">{inv.customer_name}</p>
                    <p className="text-xs text-muted-foreground">{inv.number} · {formatDateShort(inv.date)}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-sm font-semibold">{formatRupiah(inv.grand_total)}</span>
                    <StatusBadge status={inv.status} />
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>

        <div className="rounded-xl border border-border bg-card p-6 shadow-[0_2px_10px_rgba(0,0,0,0.03)]">
          <div className="mb-4 flex items-center justify-between">
            <h3 className="font-heading text-lg font-semibold">Customer Terbaru</h3>
            <Link to="/customers" className="flex items-center gap-1 text-sm font-medium text-primary hover:underline">
              Lihat semua <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </div>
          {data.recent_customers.length === 0 ? (
            <p className="py-8 text-center text-sm text-muted-foreground">Belum ada customer</p>
          ) : (
            <div className="space-y-1">
              {data.recent_customers.map((c) => (
                <Link
                  key={c.id}
                  to={`/customers/${c.id}`}
                  className="flex items-center gap-3 rounded-lg px-3 py-2.5 transition-colors hover:bg-secondary"
                >
                  <div className="flex h-9 w-9 items-center justify-center rounded-full bg-primary/10 text-sm font-semibold text-primary">
                    {(c.name || "?")[0].toUpperCase()}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium">{c.name}</p>
                    <p className="truncate text-xs text-muted-foreground">{c.company || c.whatsapp || "-"}</p>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
